import _extends from "@babel/runtime/helpers/extends";
import { usePrevious } from "@react-hookz/web";
import { contains } from "@smartsheet/lodestar-utils";
import cx from "classnames";
import differenceInDays from "date-fns/differenceInDays";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isFirstDayOfMonth from "date-fns/isFirstDayOfMonth";
import isLastDayOfMonth from "date-fns/isLastDayOfMonth";
import isSameDay from "date-fns/isSameDay";
import isSameMonth from "date-fns/isSameMonth/index.js";
import isWithinInterval from "date-fns/isWithinInterval";
import max from "date-fns/max";
import min from "date-fns/min";
import React, { forwardRef, useRef, useState, useEffect } from "react";
import { noop } from "../../../../constants/noop";
import { I18nContextProvider, useI18n } from "../../../../i18n/i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { ClickAwayListener, Flyout } from "../../../flyout";
import { useFocusMode } from "../../../focusMode";
import { VisuallyHidden } from "../../../visuallyhidden";
import { injectCatalog } from "../../i18n/DatePickerCatalogs";
import { toDatePickerDate, toDate, isValidDate, isPartialRange, getRangePickerDayLabel, formatDayLabel, isFullRange, getVisibleDateFromProps, clampDate } from "../../util/calendar";
import { useMemoizedDatePickerDate } from "../hooks/useMemoizedDatePickerDate";
import { DateRangePickerDay } from "../styled/DateRangePickerDay";
import { DateRangePickerDayBox } from "../styled/DateRangePickerDayBox";
import { DAY_STYLED_CLASS_NAME } from "./DatePickerDay";
import { DatePickerFrame } from "./DatePickerFrame";
export const DAY_START_OF_MONTH_CLASS_NAME = "lodestar-core--date-picker--start-of-month-bo7x37w";
export const DAY_END_OF_MONTH_CLASS_NAME = "lodestar-core--date-picker--end-of-month-5klq590";
const checkIfInRange = (day, start, end) => {
  return !!start && !!end && !isSameDay(day, start) && !isSameDay(day, end) && isWithinInterval(day, {
    start: min([start, end]),
    end: max([start, end])
  });
};
const getActiveRange = (activeDate, newValue) => {
  let range;
  if (isFullRange(newValue)) {
    range = [...newValue];
  } else if (isPartialRange(newValue)) {
    range = isBefore(newValue[0], activeDate) ? [newValue[0], activeDate] : [activeDate, newValue[0]];
  } else {
    range = [activeDate, activeDate];
  }
  return [...range, differenceInDays(range[1], range[0])];
};
const hasBackwardsDates = value => {
  return Array.isArray(value) && isBefore(toDate(value[1]), toDate(value[0]));
};
const isValidValueRange = value => {
  return value !== null && Array.isArray(value) && isValidDate(value[0]) && isValidDate(value[1]) && !hasBackwardsDates(value);
};
const DateRangePickerDialogBase = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    clientId,
    dayIsDisabled,
    id,
    initialPickerDate,
    locale,
    minDate,
    maxDate,
    onChange,
    onClear,
    onCloseRequested,
    popperProps,
    value,
    ...rest
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (Array.isArray(value)) {
      if (!isValidDate(value[0])) {
        console.warn("DateRangePicker: invalid start date.", value[0]);
      } else if (!isValidDate(value[1])) {
        console.warn("DateRangePicker: invalid end date.", value[1]);
      } else if (hasBackwardsDates(value)) {
        console.warn("DateRangePicker: invalid value - the end date is before the start date. Defaulting date to today.");
      }
    }
  }
  const {
    key
  } = useTheme();
  const themeMode = key;
  const {
    i18n
  } = useI18n();
  const {
    focusMode
  } = useFocusMode();
  const [a11yHelperText, setA11yHelperText] = useState("");
  const dayContainerRef = useRef(null);

  // The date the user is current interacting with.
  const [activeDate, setActiveDate] = useState(() => {
    return getVisibleDateFromProps(
    // If the value is valid, use the start date as the date to show in the dialog.
    // Otherwise, pass null here to defer to the initial picker date or today's date,
    // all clamped within minDate and maxDate.
    isValidValueRange(value) ? value?.[0] ?? null : null, initialPickerDate || null, minDate || null, maxDate || null);
  });

  // The selected range. Can be empty (null), partial([Date, null]), or full([Date, Date]).
  const [newValue, setNewValue] = useState(() => {
    if (isValidValueRange(value)) {
      return [toDate(value[0]), toDate(value[1])];
    }
    return null;
  });

  // This is the visible month in the picker, but can also be the active date to work
  // with the hotkey management in DatePickerFrame.
  // Sometimes when we change months we'll need to resync this to the active date so the
  // user's initial focus is correct.
  const [frameDate, setFrameDate] = useState(activeDate);

  // We need this to update the date when a new value is passed to the (already rendered)
  // dialog. usePrevious returns undefined on the first render, so we need to convert that to
  // null so we can compare it to the new value properly (which might be a DatePickerDay or
  // null).
  const memoizedPrevStartValue = useMemoizedDatePickerDate(usePrevious(value?.[0] ?? null) || null);
  const memoizedStartValue = useMemoizedDatePickerDate(value?.[0] || null);
  const memoizedPrevEndValue = useMemoizedDatePickerDate(usePrevious(value?.[1]) || null);
  const memoizedEndValue = useMemoizedDatePickerDate(value?.[1] || null);
  const memoizedMinDate = useMemoizedDatePickerDate(minDate || null);
  const memoizedMaxDate = useMemoizedDatePickerDate(maxDate || null);
  const memiozedInitialPickerDate = useMemoizedDatePickerDate(initialPickerDate || null);
  useEffect(() => {
    if (memoizedPrevStartValue !== memoizedStartValue || memoizedPrevEndValue !== memoizedEndValue) {
      if (memoizedStartValue && memoizedEndValue && isValidValueRange([memoizedStartValue, memoizedEndValue])) {
        const visibleStartDate = getVisibleDateFromProps(memoizedStartValue, memiozedInitialPickerDate, memoizedMinDate, memoizedMaxDate);
        const visibleEndDate = clampDate(memoizedEndValue, memoizedMinDate, memoizedMaxDate);
        setNewValue([visibleStartDate, visibleEndDate]);
        setActiveDate(visibleStartDate);
        setFrameDate(visibleStartDate);
      }
    }
  }, [memoizedPrevStartValue, memoizedStartValue, memoizedPrevEndValue, memoizedEndValue, memiozedInitialPickerDate, memoizedMinDate, memoizedMaxDate]);
  const handleApply = () => {
    if (!isFullRange(newValue)) {
      return;
    }
    onChange([toDatePickerDate(newValue[0]), toDatePickerDate(newValue[1])]);
  };
  const onDayClicked = day => {
    if (newValue === null || isFullRange(newValue)) {
      setNewValue([day, null]);
      setActiveDate(day);

      // A range has been started.
      // This will be read once, then each day will have a label that states
      // "{{Day of week/MM/DD}}, select end date"
      setA11yHelperText(i18n._( /*i18n*/{
        id: "start date selected",
        comment: "this is text the screen reader reads out after a start date has been selected in the date range picker"
      }));
    } else if (isPartialRange(newValue)) {
      const theNewValue = [newValue[0], day];
      if (isAfter(...theNewValue)) {
        theNewValue.reverse();
      }
      setNewValue(theNewValue);
      setActiveDate(theNewValue[0]);

      // This will be read once, then the user will see days that will prompt them
      // to start a new range if necessary.
      setA11yHelperText(i18n._( /*i18n*/{
        // TODO this should change since we don't have a "cancel" button anymore.
        id: "{startDate} to {endDate}, choose apply to confirm, or cancel",
        values: {
          startDate: formatDayLabel(locale, theNewValue[0]),
          endDate: formatDayLabel(locale, theNewValue[1])
        },
        comment: "this is text the screen reader reads out after a start and end date have both been selected in the date range picker"
      }));
    }
  };
  const interactiveDates = (newValue || []).concat(activeDate).filter(Boolean);
  const rangeIsSelected = isFullRange(newValue);
  const [startOfRange, endOfRange, rangeLength] = getActiveRange(activeDate, newValue);
  const clickAway = () => onCloseRequested(false);
  const closeAndRefocus = () => onCloseRequested(true);
  return /*#__PURE__*/React.createElement(I18nContextProvider, {
    locale: locale
  }, /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: clickAway
  }, /*#__PURE__*/React.createElement(Flyout, _extends({}, rest, {
    // No id on this one. We will attach all a11y-relevant
    // attributes to the DatePickerFrame.
    id: ""
    // We'll manage this elsewhere.
    ,
    onCloseRequested: noop,
    closeOnEscape: false
    // eslint-disable-next-line jsx-a11y/aria-role
    ,
    role: null,
    popperProps: {
      strategy: "fixed",
      ...popperProps
    }
  }), /*#__PURE__*/React.createElement(DatePickerFrame, _extends({
    activeDate: activeDate,
    activeMonth: frameDate,
    applyIsDisabled: !isFullRange(newValue),
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel || i18n._( /*i18n*/{
      id: "Date range picker open, select a date range",
      comment: "this is text the screen reader reads out when the date range picker is first opened"
    }),
    "aria-labelledby": ariaLabelledby,
    className: className,
    clientId: clientId,
    dayContainerRef: dayContainerRef,
    dayIsDisabled: dayIsDisabled,
    id: id,
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    onApplyClicked: handleApply,
    onClearClicked: onClear,
    onCloseRequested: closeAndRefocus,
    onSpaceOrEnter: () => onDayClicked(activeDate),
    ref: ref,
    requestDateChange: newDate => {
      setFrameDate(newDate);
      setActiveDate(newDate);
    }
  }, rest), _ref2 => {
    let {
      htmlContent,
      day,
      key,
      isDisabled,
      ...rest
    } = _ref2;
    const isStart = rangeLength > 0 && isSameDay(day, startOfRange);
    const isEnd = rangeLength > 0 && isSameDay(day, endOfRange);
    const isMonthStart = isFirstDayOfMonth(day);
    const isMonthEnd = isLastDayOfMonth(day);
    const isInRange = checkIfInRange(day, newValue?.[0] ?? null, newValue?.[1] ?? activeDate);
    return /*#__PURE__*/React.createElement(DateRangePickerDayBox, {
      key: key,
      className: cx({
        [DAY_START_OF_MONTH_CLASS_NAME]: isMonthStart,
        [DAY_END_OF_MONTH_CLASS_NAME]: isMonthEnd
      }),
      themeMode: themeMode,
      isDisabled: isDisabled,
      isStart: isStart,
      isEnd: isEnd,
      isInRange: isInRange,
      rangeIsSelected: rangeIsSelected
    }, /*#__PURE__*/React.createElement(DateRangePickerDay, _extends({}, rest, {
      className: DAY_STYLED_CLASS_NAME,
      themeMode: themeMode,
      focusMode: focusMode,
      isDisabled: isDisabled,
      "aria-label": getRangePickerDayLabel(i18n, locale, day, newValue),
      tabIndex: interactiveDates.some(d => isSameDay(d, day)) ? 0 : -1,
      isStart: isStart,
      isEnd: isEnd,
      rangeIsSelected: rangeIsSelected,
      isInRange: isInRange,
      isSelected: (newValue || []).filter(Boolean).some(d => isSameDay(d, day)) ?? false,
      onClick: () => {
        !isDisabled && onDayClicked(day);
      },
      onBlur: event => {
        // on blur, we'll update this so we get rid of the extra focused day.
        // this means we'll only have 1-2 focusable days after tabbing
        if (!contains(dayContainerRef.current, event.relatedTarget)) {
          // If focus leaves the day grid, reset the active date to the start or the
          // end date of the selected range if they exists and are in this month.
          if (newValue?.[0] && isSameMonth(frameDate, newValue[0])) {
            setActiveDate(newValue[0]);
          } else if (newValue?.[1] && isSameMonth(frameDate, newValue[1])) {
            setActiveDate(newValue[1]);
          }
        }
      },
      onFocus: () => {
        // on focusing on of the ends of a range, we'll update this so we get rid of the extra focused day.
        // this means we'll only have 1-2 focusable days after tabbing.
        if (newValue?.[0] && isSameDay(day, newValue[0])) {
          setActiveDate(newValue[0]);
        } else if (newValue?.[1] && isSameDay(day, newValue[1])) {
          setActiveDate(newValue[1]);
        }
      },
      onMouseEnter: () => {
        // If we have an initial selection, set the active day to the
        // hovered date.
        if (!isDisabled && isPartialRange(newValue)) {
          setActiveDate(day);
        }
      }
    }), htmlContent));
  }), /*#__PURE__*/React.createElement(VisuallyHidden, {
    "aria-live": "polite"
  }, a11yHelperText))));
});
DateRangePickerDialogBase.displayName = "DateRangePickerDialogBase";
export const DateRangePickerDialog = /*#__PURE__*/forwardRef((props, ref) => {
  injectCatalog();
  return /*#__PURE__*/React.createElement(I18nContextProvider, {
    locale: props.locale
  }, /*#__PURE__*/React.createElement(DateRangePickerDialogBase, _extends({}, props, {
    ref: ref
  })));
});
DateRangePickerDialog.displayName = "DateRangePickerDialog";
//# sourceMappingURL=DateRangePickerDialog.js.map